// MQL5 EA Import — browser data layer.
//
// Mirrors signalDataFeeds.js: same API base resolution, same fetch wrapper, same error shape
// (message plus the server's structured payload, so the page can render a capability report rather
// than a bare string).

const LOCAL_HOST_PATTERN = /^(localhost|127\.0\.0\.1)$/i;
const API_BASE_URL = (() => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && LOCAL_HOST_PATTERN.test(window.location.hostname)) {
    return 'http://localhost:5000';
  }
  return '';
})();

function authToken() {
  return typeof window !== 'undefined' ? localStorage.getItem('userToken') : null;
}

async function postImport(path, body) {
  const token = authToken();
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }
  if (!response.ok) {
    // A 422 carries a real capability report, which is the useful part of the failure — surface the
    // whole payload rather than collapsing it to a message.
    const failure = new Error(payload?.error || `Request failed (${response.status})`);
    failure.status = response.status;
    failure.payload = payload;
    throw failure;
  }
  return payload;
}

// Compile + link only. Fast, no market data touched.
export function analyzeEa({ files, entry }) {
  return postImport('/api/mql5-import/analyze', { files, entry });
}

// Analyze, then run over real market data.
//
// Always DETACHED. A heavy EA can take half an hour — DeepTrader rescans its whole lookback on every
// tick and measured 420ms per bar — and no browser or proxy holds a POST open that long. The server
// answers 202 with a job id, the run continues without us, and we collect the result when the
// progress channel says it is finished. Fast runs feel identical: the first poll usually has it.
export async function backtestEa(options, { onProgress, signal } = {}) {
  const started = await postImport('/api/mql5-import/backtest', { ...options, background: true });
  // A server without the background path answered with the result itself; take it as-is.
  if (!started?.background || !started?.jobId) return started;

  const jobId = started.jobId;
  const progressId = started.progressId || jobId;
  for (;;) {
    if (signal?.aborted) throw new Error('Cancelled.');
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => { setTimeout(resolve, 900); });
    // eslint-disable-next-line no-await-in-loop
    const progress = await fetchImportProgress(progressId);
    if (progress && typeof onProgress === 'function') onProgress(progress);
    if (progress && (progress.phase === 'done' || progress.phase === 'failed')) break;
    // 'unknown' means the entry has expired or the server restarted mid-run. Fall through to the
    // result endpoint, which answers definitively rather than leaving the UI spinning forever.
    if (progress && progress.phase === 'unknown') break;
  }
  return fetchImportResult(jobId);
}

// Collect a finished run. 202 means it is somehow still going, so keep waiting rather than
// reporting a result that does not exist yet.
export async function fetchImportResult(jobId) {
  const token = authToken();
  for (let attempt = 0; attempt < 600; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await fetch(`${API_BASE_URL}/api/mql5-import/result/${encodeURIComponent(jobId)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    let payload = null;
    try {
      // eslint-disable-next-line no-await-in-loop
      payload = await response.json();
    } catch (error) {
      payload = null;
    }
    if (response.status === 202) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { setTimeout(resolve, 1500); });
      continue;
    }
    if (!response.ok) {
      const failure = new Error(payload?.error || `Run failed (${response.status})`);
      failure.status = response.status;
      failure.payload = payload;
      throw failure;
    }
    return payload;
  }
  throw new Error('The run is taking longer than expected. It may still be going on the server.');
}

// What the run is doing right now. The backtest is one long POST, so progress travels on a second
// channel keyed by an id the CLIENT mints — see RUN_PROGRESS in the service. A failed poll is not
// an error worth showing: the run itself is still reported by the POST.
export async function fetchImportProgress(progressId) {
  const token = authToken();
  const response = await fetch(`${API_BASE_URL}/api/mql5-import/progress/${encodeURIComponent(progressId)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) return null;
  return response.json();
}

// Ask the server to stop a run. It answers straight away; the run itself stops at its next yield
// and returns the trades it completed, so a cancel is not a discard.
export async function cancelImportRun(progressId) {
  const token = authToken();
  const response = await fetch(`${API_BASE_URL}/api/mql5-import/cancel/${encodeURIComponent(progressId)}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return response.ok;
}

export function newProgressId() {
  return `run-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Read the uploaded files in the browser. MetaEditor frequently writes UTF-16LE, so decode by
// sniffing the BOM — read as UTF-8 those files arrive NUL-separated and fail to parse for no
// reason the user could possibly diagnose.
export function readSourceFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.onload = () => {
      const buffer = new Uint8Array(reader.result);
      let content;
      if (buffer.length >= 2 && buffer[0] === 0xFF && buffer[1] === 0xFE) {
        content = new TextDecoder('utf-16le').decode(buffer.subarray(2));
      } else if (buffer.length >= 2 && buffer[0] === 0xFE && buffer[1] === 0xFF) {
        content = new TextDecoder('utf-16be').decode(buffer.subarray(2));
      } else {
        // No BOM: a high proportion of NUL bytes still means UTF-16LE.
        let nulls = 0;
        const head = buffer.subarray(0, Math.min(512, buffer.length));
        for (let i = 1; i < head.length; i += 2) if (head[i] === 0x00) nulls += 1;
        content = nulls > head.length / 4
          ? new TextDecoder('utf-16le').decode(buffer)
          : new TextDecoder('utf-8').decode(buffer);
      }
      resolve({ name: file.name, content });
    };
    reader.readAsArrayBuffer(file);
  });
}

// Symbols and timeframes come from the SERVER, so the picker can only ever offer instruments the
// platform can actually load. A hardcoded list drifts the moment the catalogue changes.
export async function fetchImportMarkets() {
  const token = authToken();
  const response = await fetch(`${API_BASE_URL}/api/mql5-import/markets`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error(`Could not load the symbol list (${response.status})`);
  return response.json();
}

// Fallback only, for the moment before the fetch resolves.
export const MQL5_IMPORT_TIMEFRAMES = [
  'M1', 'M2', 'M3', 'M4', 'M5', 'M6', 'M10', 'M12', 'M15', 'M20', 'M30',
  'H1', 'H2', 'H3', 'H4', 'H6', 'H8', 'H12', 'D1', 'W1', 'MN1',
];
